// =============================================================================
//  tiemposDeLinea.js
//  HASTA DÓNDE PUEDE MOVERSE EL PRINCIPIO Y EL FINAL DE UNA LÍNEA
//
//  Rob, 07-10-2026: «si cambio por ejemplo el final de un segmento, éste le pega
//  directamente al inicio del siguiente... en fin, todo como en vídeo pero en
//  Sync Editor».
//
//  ── LO QUE CAMBIÓ EL MISMO DÍA ──────────────────────────────────────────────
//  La primera versión de esto FRENABA: calculaba hasta dónde podía llegar cada
//  corte sin comerse a su vecina y recortaba ahí. Duró unas horas. Rob: «le di a
//  +0.5 y ya no me dejó. Como que me castigó y dijo: hasta aquí es mi límite».
//
//  Frenar protege a la vecina y rompe el trabajo. Si el verso dura de verdad
//  hasta el 0:31.5, la línea de abajo EMPIEZA en el 0:31.5 — su tiempo de ahora
//  está mal, y es lo que hay que corregir, no lo que hay que defender. Y pasa
//  con facilidad: en un transcript de YouTube hay versos a una décima unos de
//  otros, así que el primero de la fila bloqueaba a todos los demás.
//
//  Así que ahora se EMPUJA — ver `acomoda`. Lo que queda de aquello es
//  `limitesDeLinea`, que ya no prohíbe nada: sólo dice dónde están las vecinas,
//  para poder pintarlo.
//
//  En una canción una línea NO guarda su final: guarda sólo su `time`, y el
//  final es el principio de la siguiente. Por eso mover el FINAL de la línea 4 es
//  literalmente mover el PRINCIPIO de la 5 — no hay dos números, hay uno. Eso ya
//  estaba bien montado; lo que faltaba eran los topes.
//
//  ── POR QUÉ HACEN FALTA LOS TOPES ───────────────────────────────────────────
//  Sin ellos, arrastrar el principio de una línea podía pasarlo por encima de la
//  siguiente y dejarla con duración negativa. Eso no se ve en el editor: se ve
//  después, cuando el alumno oye la frase y el hueco le sale dos líneas más
//  tarde. Lo mismo pasaba en los vídeos y se arregló igual.
//
//  MINIMO es lo que tiene que quedarle a la vecina para seguir existiendo. Si
//  hiciera falta más, se mueve la vecina: lo que no puede es desaparecer porque
//  alguien arrastró de más en otro sitio.
//
//  ── POR QUÉ ESTÁ SUELTO DEL DIBUJO ──────────────────────────────────────────
//  Porque es la cuenta que decide si una canción queda bien o queda descuadrada,
//  y suelta se puede medir sin navegador. Y porque TODOS los caminos —las
//  flechas, los botones de ±0.1, el arrastre y la casilla donde se teclea el
//  tiempo— tienen que pasar por aquí. Mientras cada uno tenía su propio recorte
//  con números distintos, unos respetaban a la vecina y otros no.
// =============================================================================

/** Lo que tiene que quedarle a una línea para seguir existiendo. */
export const MINIMO = 0.3;

/** Décimas: es lo más fino que el reloj de YouTube puede cumplir de verdad. */
export const r1 = (s) => Math.round((Number(s) || 0) * 10) / 10;

const aNumero = (v, siNo = null) => {
    //  `Number(null)` es 0, y `Number('')` también. Sin este portero, una línea
    //  que todavía no se ha marcado —se pegan las letras y se van marcando una a
    //  una, así que a medio trabajo hay muchas— contaba como el segundo cero: era
    //  el suelo de la de abajo y el techo de la de arriba, y los topes salían de
    //  un tiempo que nadie había puesto.
    if (v === null || v === undefined || v === '') return siNo;
    const n = Number(v);
    return Number.isFinite(n) ? n : siNo;
};

const tiempoDe = (lines, i) => {
    const l = Array.isArray(lines) ? lines[i] : null;
    return l ? aNumero(l.time) : null;
};

/**
 * Hasta dónde puede ir el principio y el final de la línea `idx`.
 *
 * `end` viene a null cuando la línea es la última: entonces no tiene final
 * propio que mover, porque no hay siguiente cuyo principio sea su final.
 */
export function limitesDeLinea(lines, idx, duracion = 0) {
    const inicio = tiempoDe(lines, idx);
    const anterior = tiempoDe(lines, idx - 1);
    const siguiente = tiempoDe(lines, idx + 1);
    const laDeDespues = tiempoDe(lines, idx + 2);
    const total = aNumero(duracion, 0) || 0;

    if (inicio === null) return { start: { min: 0, max: 0 }, end: null };

    //  El principio no puede comerse a la anterior ni pasarse de su propio
    //  final. Sin siguiente, el final es el final de la canción.
    const finActual = siguiente !== null ? siguiente : (total || inicio + MINIMO);
    const start = {
        min: anterior !== null ? r1(anterior + MINIMO) : 0,
        max: r1(Math.max(anterior !== null ? anterior + MINIMO : 0, finActual - MINIMO)),
    };

    if (siguiente === null) return { start, end: null };

    //  El final —que es el principio de la siguiente— no puede subirse encima de
    //  esta línea ni comerse a la que viene después.
    const end = {
        min: r1(inicio + MINIMO),
        max: laDeDespues !== null
            ? r1(Math.max(inicio + MINIMO, laDeDespues - MINIMO))
            : r1(Math.max(inicio + MINIMO, total || siguiente + MINIMO)),
    };

    return { start, end };
}

/**
 * Cuánto desfase global CABE de verdad.
 *
 * El desfase global mueve la canción entera. Con un número negativo grande, las
 * primeras líneas chocan contra el cero — y `Math.max(0, …)` las aplasta todas
 * ahí: tres líneas distintas acaban en 0:00.0, en el mismo sitio y sin orden.
 * Eso ya no se arregla con el botón de quitar el desfase, porque la información
 * de dónde estaba cada una se perdió al aplastarlas.
 *
 * Así que el desfase se recorta ANTES de aplicarlo: se mueve lo que se pueda
 * mover sin que la primera línea se salga por abajo.
 */
export function desfaseQueCabe(lines, delta) {
    const d = Number(delta);
    if (!Number.isFinite(d)) return 0;
    const tiempos = (Array.isArray(lines) ? lines : [])
        .map((l) => aNumero(l?.time))
        .filter((t) => t !== null);
    if (!tiempos.length) return 0;
    if (d >= 0) return r1(d);
    //  La más temprana marca el suelo: ninguna puede quedar por debajo de cero.
    return r1(Math.max(d, -Math.min(...tiempos)));
}

/**
 * Qué líneas quedaron fuera de orden: una que empieza igual o después que la
 * siguiente.
 *
 * Una canción así se guarda sin protestar y se ve bien en el editor. Lo que
 * pasa es que el alumno recibe los huecos cambiados de sitio, o una línea que
 * dura menos que nada. Esto es la última red antes de guardar.
 */
export function lineasDesordenadas(lines) {
    const l = Array.isArray(lines) ? lines : [];
    const malas = [];
    for (let i = 0; i < l.length - 1; i++) {
        const a = aNumero(l[i]?.time);
        const b = aNumero(l[i + 1]?.time);
        if (a === null || b === null) continue;
        if (b <= a) malas.push({ idx: i, time: a, siguiente: b });
    }
    return malas;
}

/**
 * Mover una línea y HACERLE SITIO empujando a las vecinas lo justo.
 *
 * Rob, 07-10-2026: «necesita todavía unos segundos más para cubrir la parte de
 * la letra. Le di a +0.5 y ya no me dejó. Como que me castigó y dijo: hasta aquí
 * es mi límite y de aquí no me muevo más... si esto no se logra, la precisión es
 * un error grande y la plataforma no funciona».
 *
 * Tenía razón, y el error era de diseño mío. Frenar en seco protege a la vecina
 * y rompe el trabajo: si el verso dura de verdad hasta el 0:31.5, la línea de
 * abajo EMPIEZA en el 0:31.5 — su tiempo de ahora está mal, y es lo que hay que
 * corregir, no lo que hay que defender.
 *
 * Y pasa con facilidad: en un transcript de YouTube hay versos a una décima
 * unos de otros, y con los mínimos encadenados el primero de la fila bloquea a
 * todos los demás sin que se vea por qué.
 *
 * Así que ahora se empuja. Sólo a las que de verdad se quedarían sin sitio, y
 * sólo lo justo para que les quepa el mínimo. Las demás no se tocan — que es la
 * diferencia con el desfase global, que mueve todo porque sí.
 *
 * Devuelve las líneas ya acomodadas y CUÁLES se movieron además de la pedida,
 * porque hay que poder decirlo y hay que poder deshacerlo.
 */
export function acomoda(lines, idx, nuevoTiempo, duracion = 0) {
    const origen = Array.isArray(lines) ? lines : [];
    if (idx < 0 || idx >= origen.length) return { lines: origen, movidas: [] };

    const total = aNumero(duracion, 0) || 0;
    const techo = total > 0 ? total : Infinity;
    const tiempos = origen.map((l) => aNumero(l?.time));

    //  Se trabaja SÓLO con las que tienen tiempo. A medio trabajo hay muchas sin
    //  marcar —se pegan las letras y se van marcando una a una—, y si se
    //  recorriera la lista entera, un hueco cortaba la cadena: la de arriba del
    //  hueco no se enteraba de que la de abajo se le había echado encima.
    const conTiempo = [];
    tiempos.forEach((t, j) => { if (t !== null) conTiempo.push(j); });
    const sitio = conTiempo.indexOf(idx);
    if (sitio === -1) return { lines: origen, movidas: [] };

    tiempos[idx] = r1(Math.min(Math.max(aNumero(nuevoTiempo, 0), 0), techo));

    //  Hacia abajo: a cada una que se quede sin sitio se le da el mínimo.
    for (let k = sitio + 1; k < conTiempo.length; k++) {
        const aqui = conTiempo[k];
        const suelo = r1(tiempos[conTiempo[k - 1]] + MINIMO);
        if (tiempos[aqui] < suelo) tiempos[aqui] = suelo;
    }

    //  Hacia arriba: igual, pero al revés.
    for (let k = sitio - 1; k >= 0; k--) {
        const aqui = conTiempo[k];
        const tope = r1(tiempos[conTiempo[k + 1]] - MINIMO);
        if (tiempos[aqui] > tope) tiempos[aqui] = tope;
    }

    //  Empujar hacia abajo puede sacar a las últimas del final de la canción, y
    //  empujar hacia arriba puede sacar a las primeras por debajo del cero. Nadie
    //  suena antes de que empiece ni después de que acabe, así que se apoyan en
    //  el borde y se vuelve a repartir hacia el otro lado. Una pasada basta:
    //  cada una sólo separa.
    if (techo !== Infinity && tiempos[conTiempo[conTiempo.length - 1]] > techo) {
        for (let k = conTiempo.length - 1; k >= 0; k--) {
            const aqui = conTiempo[k];
            const tope = k === conTiempo.length - 1
                ? techo
                : r1(tiempos[conTiempo[k + 1]] - MINIMO);
            if (tiempos[aqui] > tope) tiempos[aqui] = tope;
        }
    }
    if (tiempos[conTiempo[0]] < 0) {
        for (let k = 0; k < conTiempo.length; k++) {
            const aqui = conTiempo[k];
            const suelo = k === 0 ? 0 : r1(tiempos[conTiempo[k - 1]] + MINIMO);
            if (tiempos[aqui] < suelo) tiempos[aqui] = suelo;
        }
    }

    const movidas = [];
    const nuevas = origen.map((l, j) => {
        if (tiempos[j] === null || tiempos[j] === aNumero(l?.time)) return l;
        if (j !== idx) movidas.push(j);
        return { ...l, time: tiempos[j] };
    });
    return { lines: nuevas, movidas };
}
